import * as React from 'react'

import {
  Box,
  RadioButton,
  Spinner,
  Text,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import TextField from 'modules/core/components/TextField'
import { useCallback, useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import i18n from 'modules/core/lib/i18n'

import {
  WizardData,
  canRenameFile,
} from 'modules/documents/components/BulkFiles/DocumentLocalStepsModal/types'
import filesStore from 'modules/documents/components/Documents/store'
import { getPdfPages } from 'modules/documents/components/PdfViewer/pdfUtils'
import { ALL_UPLOAD_TYPES } from 'modules/documents/lib/constants'
import { splitFileName } from 'modules/documents/lib/files/filenameUtils'

const COMBINE_COUNT = 1

const SplittingSettings = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { has_split_documents_by_page } = useFeatures(['split_documents_by_page'])
  const { uploadType, numberSplitPages } = data
  const [loading, setLoading] = React.useState(true)
  const [docsToSend, setDocsToSend] = React.useState(filesStore.files.length)
  const [pagesPerFile, setPagesPerFile] = React.useState<number[]>([])
  const maxPages = Math.max(...pagesPerFile) - 1

  const numOfDocs = useCallback(
    (arrayOfPages: number[]) => {
      const totalDocuments = arrayOfPages.reduce(
        (acc, numPages) => acc + Math.ceil(numPages / numberSplitPages),
        0
      )
      return totalDocuments
    },
    [numberSplitPages]
  )

  const changeUploadType = useUnsafeCallback(
    (type: (typeof ALL_UPLOAD_TYPES)[keyof typeof ALL_UPLOAD_TYPES]) => {
      const splittedFilename =
        canRenameFile(type, filesStore.files.length) && filesStore.files[0]?.name
          ? splitFileName(filesStore.files[0]?.name)
          : undefined

      onDataChanged({
        ...data,
        uploadType: type,
        combineFileName: splittedFilename?.fileName,
      })
    },
    [data, onDataChanged]
  )

  useEffect(() => {
    const asyncGetPagesPerFile = async () => {
      setLoading(true)
      const pagesPerPDF = await Promise.all(
        filesStore.files.map(async (file) => {
          const numOfPages = await getPdfPages(file)
          return numOfPages
        })
      )
      setPagesPerFile(pagesPerPDF)
      setLoading(false)
    }

    asyncGetPagesPerFile()
  }, [])

  useUnsafeEffect(() => {
    const areAllSinglePages =
      pagesPerFile.filter((pages) => pages === 1).length === filesStore.files.length

    switch (uploadType) {
      case ALL_UPLOAD_TYPES.split:
        setDocsToSend(numOfDocs(pagesPerFile))
        if (areAllSinglePages) {
          changeUploadType(ALL_UPLOAD_TYPES.do_not_touch)
        }
        break
      case ALL_UPLOAD_TYPES.combine:
        setDocsToSend(COMBINE_COUNT)
        break
      default:
        setDocsToSend(filesStore.files.length)
        break
    }
  }, [uploadType, pagesPerFile, numOfDocs, changeUploadType])

  const uploadTypes = React.useRef([
    {
      label: i18n.t('split_documents.form.do_not_touch'),
      value: ALL_UPLOAD_TYPES.do_not_touch,
    },
    {
      label: i18n.t('split_documents.form.split'),
      value: ALL_UPLOAD_TYPES.split,
    },
    {
      label: i18n.t('split_documents.form.combine'),
      value: ALL_UPLOAD_TYPES.combine,
    },
  ]).current

  const onChangeSplitNumber = (value: number) => {
    const minMaxSplitNumber = Math.max(1, Math.min(value, maxPages))

    onDataChanged({
      ...data,
      numberSplitPages: minMaxSplitNumber,
    })
  }

  const renderTypeOptions = (type: (typeof ALL_UPLOAD_TYPES)[keyof typeof ALL_UPLOAD_TYPES]) => {
    if (type === ALL_UPLOAD_TYPES.combine || type === ALL_UPLOAD_TYPES.do_not_touch) {
      return (
        <Text size="200" weight="normal">
          {type === ALL_UPLOAD_TYPES.combine
            ? i18n.t('split_documents.form.combine_description')
            : i18n.t('split_documents.form.do_not_touch_description')}
          {}
        </Text>
      )
    }

    return (
      <>
        <Text size="200" weight="normal">
          {has_split_documents_by_page
            ? i18n.t('split_documents.form.split_description_pages')
            : i18n.t('split_documents.form.split_description')}
        </Text>
        {has_split_documents_by_page && (
          <Box>
            <TextField
              label={i18n.t('split_documents.split_options.title')}
              value={numberSplitPages.toString()}
              onChange={(value) => onChangeSplitNumber(Number(value))}
              type="number"
              min={1}
              max={maxPages}
              onFocus={(e) => e.target.select()}
            />
          </Box>
        )}
      </>
    )
  }

  const isDisabledOption = (type: (typeof ALL_UPLOAD_TYPES)[keyof typeof ALL_UPLOAD_TYPES]) =>
    (type === ALL_UPLOAD_TYPES.split && pagesPerFile.every((pages) => pages <= numberSplitPages)) ||
    (type === ALL_UPLOAD_TYPES.combine && filesStore.files.length === 1)

  return (
    <Box width="s400" padding="s24">
      <Box gap="s16">
        <Box gap="s8">
          <Text size="200" weight="medium" color="grey1000">
            {i18n.t('split_documents.form.split_settings')}
          </Text>
          <Box
            width="full"
            borderRadius={{ all: 'abs008' }}
            paddingY="s12"
            paddingX="s16"
            gap="s16"
            border={{
              all: {
                width: 's2',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            {uploadTypes.map(({ label, value }, index) => {
              return (
                <Box key={index} gap="s2">
                  <RadioButton
                    label={label}
                    value={value}
                    checked={value === uploadType}
                    size="small"
                    onChange={() => changeUploadType(value)}
                    disabled={loading || isDisabledOption(value)}
                  />
                  <Box paddingLeft="s24" gap="s8">
                    {renderTypeOptions(value)}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
        <Box
          width="full"
          borderRadius={{ all: 'abs008' }}
          padding="s16"
          gap="s12"
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          {loading && (
            <Box alignItems="center">
              <Spinner size="s24" />
            </Box>
          )}
          {!loading && (
            <>
              <Box flexDirection="row" justifyContent="spaceBetween" gap="s16">
                <Text size="300" weight="semibold">
                  {i18n.t('split_documents.sidepanel.summary.docs_to_send')}
                </Text>
                <Text size="400" weight="semibold">
                  {docsToSend}
                </Text>
              </Box>
              <Box flexDirection="row" justifyContent="spaceBetween" gap="s16">
                <Text size="200">{i18n.t('split_documents.sidepanel.summary.uploaded_docs')}</Text>
                <Text size="200" weight="semibold">
                  {filesStore.files.length}
                </Text>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default SplittingSettings
